import { useState, useEffect } from "react";
import { 
  Calendar, Mail, Users, Link, CheckCircle, XCircle, LogOut, Clock, Building, Building2,
  ExternalLink, RefreshCw, Settings, ChevronDown, ChevronUp, Linkedin,
  Globe, Phone, Briefcase, MessageSquare, Webhook, Save, Play, AlertCircle,
  Copy, FileText, BarChart3, Trash2, Eye, EyeOff, TrendingUp, Search, UserCircle,
  FolderOpen, Plus, Pencil, Image, X, GripVertical, Shield, Calculator, ArrowRight, Send, Edit
} from "lucide-react";
import AngebotsKalkulator from "../components/admin/AngebotsKalkulator";
import ROICalculator from "../components/admin/ROICalculator";
import TestimonialManager from "../components/admin/TestimonialManager";
import { NewsletterManager } from "../components/admin/NewsletterManager";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "lab336admin";

const SECTION_LABELS = {
  hero: "Hero", services: "Services", software: "Software-Showcase",
  scheduler: "Terminplaner", testimonials: "Testimonials", clients: "Kunden",
  "case-studies": "Fallstudien", blog: "Blog", newsletter: "Newsletter"
};

const BRIEFING_SECTIONS = [
  { id: "overview", title: "01 Überblick", keys: [
    { key: "primary_goal", label: "Primäres Ziel" },
    { key: "primary_conversion", label: "Primäre Conversion" },
    { key: "target_audience", label: "Zielgruppe" },
    { key: "go_live_date", label: "Go-live Datum" },
    { key: "traffic_sources", label: "Traffic-Quellen" },
    { key: "contact_name", label: "Ansprechpartner" },
    { key: "contact_email", label: "E-Mail" },
    { key: "decision_maker", label: "Entscheider" },
  ]},
  { id: "goals", title: "02 Ziele & KPIs", keys: [
    { key: "business_goal", label: "Business-Ziel" },
    { key: "kpi_1", label: "KPI 1" },
    { key: "kpi_2", label: "KPI 2" },
    { key: "kpi_3", label: "KPI 3" },
    { key: "conversion_value", label: "Conversion-Wert" },
    { key: "existing_benchmarks", label: "Benchmarks" },
  ]},
  { id: "offering", title: "03 Angebot & Value Prop", keys: [
    { key: "offering_description", label: "Angebot" },
    { key: "benefit_1", label: "Nutzen #1" },
    { key: "benefit_2", label: "Nutzen #2" },
    { key: "benefit_3", label: "Nutzen #3" },
    { key: "differentiation", label: "Differenzierung" },
    { key: "sales_objections", label: "Einwände" },
    { key: "risk_reducers", label: "Risikoreduzierer" },
  ]},
  { id: "bonus", title: "Bonus — Copy Inputs", keys: [
    { key: "positioning_statement", label: "Positionierung" },
    { key: "proof_a", label: "Proof A" },
    { key: "proof_b", label: "Proof B" },
    { key: "proof_c", label: "Proof C" },
  ]},
];


function AdminReplyBox({ projectId, offerNumber, questionIndex, onReplied }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [show, setShow] = useState(false);
  if (!show) return (
    <button onClick={() => setShow(true)} className="mt-1.5 ml-5 text-[0.65rem] text-lab-accent hover:underline flex items-center gap-1" data-testid={`reply-btn-${offerNumber}-${questionIndex}`}>
      <Send className="w-2.5 h-2.5" /> Antworten
    </button>
  );
  return (
    <div className="mt-1.5 ml-5 flex gap-2 items-start" data-testid={`reply-form-${offerNumber}-${questionIndex}`}>
      <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Antwort eingeben..."
        className="flex-1 border border-lab-border px-2 py-1 text-xs focus:border-lab-accent outline-none" />
      <button disabled={sending || !reply.trim()} onClick={async () => {
        setSending(true);
        try {
          await axios.post(`${API}/admin/projects/${projectId}/offers/${offerNumber}/reply`, { reply: reply.trim(), question_index: questionIndex });
          toast.success("Antwort gesendet");
          setReply(""); setShow(false);
          if (onReplied) onReplied();
        } catch (e) { console.error(e); toast.error("Fehler beim Senden"); }
        finally { setSending(false); }
      }} className="px-3 py-1 text-xs font-semibold bg-lab-primary text-white hover:bg-lab-primary/90 disabled:opacity-50"
        data-testid={`reply-send-${offerNumber}-${questionIndex}`}>
        {sending ? "..." : "Senden"}
      </button>
      <button onClick={() => { setShow(false); setReply(""); }} className="text-lab-text-muted hover:text-lab-primary text-xs px-1">✕</button>
    </div>
  );
}

const PROJECT_STATUSES = [
  { value: "kickoff_pending", label: "Kickoff ausstehend", color: "bg-yellow-50 text-yellow-700" },
  { value: "in_progress", label: "In Umsetzung", color: "bg-blue-50 text-blue-700" },
  { value: "review", label: "Review", color: "bg-purple-50 text-purple-700" },
  { value: "completed", label: "Abgeschlossen", color: "bg-green-50 text-green-700" },
  { value: "on_hold", label: "Pausiert", color: "bg-gray-100 text-gray-500" },
];

function PortalBriefing({ detail, onAddNote, onStatusChange }) {
  const [noteText, setNoteText] = useState("");
  const sub = detail.submissions[0];
  const fd = sub?.form_data || {};
  const project = detail.project;

  return (
    <div className="border-t border-lab-border bg-lab-bg-light">
      {/* Header bar */}
      <div className="p-4 flex items-center justify-between border-b border-lab-border bg-white">
        <div className="flex items-center gap-3">
          <span className="text-sm text-lab-text-muted">Passwort:</span>
          <code className="text-xs font-mono bg-lab-bg-light px-2 py-0.5 border border-lab-border">{project.password}</code>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-lab-text-muted">Projekt-Status:</span>
          <select
            value={project.project_status || "kickoff_pending"}
            onChange={e => onStatusChange(e.target.value)}
            className="text-xs border border-lab-border px-2 py-1 bg-white"
            data-testid="project-status-select"
          >
            {PROJECT_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {!sub ? (
        <div className="p-6 text-sm text-lab-text-muted">Noch keine Daten vom Kunden eingegangen.</div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Status badge */}
          <div className="flex items-center gap-3 text-xs text-lab-text-muted">
            <span className={`px-2 py-0.5 ${sub.status === "submitted" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
              {sub.status === "submitted" ? "Eingereicht" : "In Bearbeitung"}
            </span>
            <span>Aktualisiert: {new Date(sub.updated_at).toLocaleString("de-CH")}</span>
          </div>

          {/* Structured sections */}
          <div className="grid lg:grid-cols-2 gap-4">
            {BRIEFING_SECTIONS.map(section => {
              const hasData = section.keys.some(f => fd[f.key]);
              if (!hasData) return null;
              return (
                <div key={section.id} className="bg-white border border-lab-border p-4">
                  <h4 className="text-sm font-semibold text-lab-accent mb-3 pb-2 border-b border-lab-border">{section.title}</h4>
                  <div className="space-y-2">
                    {section.keys.map(field => {
                      const val = fd[field.key];
                      if (!val) return null;
                      return (
                        <div key={field.key} className="text-sm">
                          <span className="text-lab-text-muted text-xs block">{field.label}</span>
                          <span className="text-lab-text">{Array.isArray(val) ? val.join(", ") : String(val)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Files */}
          {detail.files.length > 0 && (
            <div className="bg-white border border-lab-border p-4">
              <h4 className="text-sm font-semibold text-lab-accent mb-3 pb-2 border-b border-lab-border">Dateien ({detail.files.length})</h4>
              <div className="space-y-2">
                {detail.files.map(file => (
                  <div key={file.id} className="flex items-center gap-3 text-sm">
                    <FileText className="w-4 h-4 text-lab-accent shrink-0" />
                    <span className="truncate flex-1">{file.filename}</span>
                    <span className="text-xs text-lab-text-muted">{(file.size / 1024).toFixed(0)} KB</span>
                    {file.url && <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs text-lab-accent hover:underline">Download</a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          <div className="bg-white border border-lab-border p-4">
            <h4 className="text-sm font-semibold text-lab-accent mb-3 pb-2 border-b border-lab-border">Interne Notizen</h4>
            {(project.admin_notes || []).map(note => (
              <div key={note.id} className="text-sm mb-2 p-2 bg-lab-bg-light border-l-2 border-lab-accent">
                <p className="text-lab-text">{note.text}</p>
                <span className="text-xs text-lab-text-muted">{new Date(note.created_at).toLocaleString("de-CH")}</span>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <Input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                className="rounded-none text-sm flex-1"
                placeholder="Notiz hinzufügen..."
                onKeyDown={e => {
                  if (e.key === "Enter" && noteText.trim()) {
                    onAddNote(noteText.trim());
                    setNoteText("");
                  }
                }}
                data-testid="portal-note-input"
              />
              <button
                onClick={() => { if (noteText.trim()) { onAddNote(noteText.trim()); setNoteText(""); } }}
                className="btn-primary rounded-none px-3 text-sm"
                data-testid="portal-note-add"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("portal");
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [editSubscriberEmail, setEditSubscriberEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [expandedAppointment, setExpandedAppointment] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [receivedWebhooks, setReceivedWebhooks] = useState([]);
  // Blog management
  const [cachedBlogs, setCachedBlogs] = useState([]);
  const [refreshingBlogs, setRefreshingBlogs] = useState(false);
  const [cronSettings, setCronSettings] = useState({ enabled: false, interval_hours: 6, last_run: "" });
  // Statistics
  const [stats, setStats] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  // SEO
  const [seoSettings, setSeoSettings] = useState(null);
  const [savingSeo, setSavingSeo] = useState(false);
  // About
  const [aboutSettings, setAboutSettings] = useState(null);
  const [savingAbout, setSavingAbout] = useState(false);
  // Portfolio
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  // Portal
  const [portalProjects, setPortalProjects] = useState([]);
  const [newProject, setNewProject] = useState({ client_name: "", client_email: "", password: "", notes: "" });
  const [creatingProject, setCreatingProject] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [portalWebhookUrl, setPortalWebhookUrl] = useState("");
  const [projectDocs, setProjectDocs] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(null);
  const [socialPosts, setSocialPosts] = useState([]);
  // Company Settings (Firmendaten)
  const [companySettings, setCompanySettings] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => {
    // Check if already authenticated (session)
    const auth = sessionStorage.getItem("lab336_admin");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("lab336_admin", "true");
      toast.success("Angemeldet!");
    } else {
      toast.error("Falsches Passwort");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("lab336_admin");
  };

  const loadNewsletters = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletters`);
      setNewsletters(res.data);
    } catch (e) { console.error("Newsletter load error:", e); }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load calendar status
      const statusRes = await axios.get(`${API}/oauth/calendar/status`);
      setCalendarStatus(statusRes.data);

      // Load appointments
      const appointmentsRes = await axios.get(`${API}/admin/appointments`);
      setAppointments(appointmentsRes.data);

      // Load contacts
      const contactsRes = await axios.get(`${API}/admin/contacts`);
      setContacts(contactsRes.data);

      // Load newsletters
      const newslettersRes = await axios.get(`${API}/admin/newsletters`);
      setNewsletters(newslettersRes.data);

      // Load settings
      const settingsRes = await axios.get(`${API}/admin/settings`);
      setWebhookUrl(settingsRes.data.webhook_url || "");

      // Load webhook logs
      const logsRes = await axios.get(`${API}/admin/webhook-logs`);
      setWebhookLogs(logsRes.data || []);

      // Load received webhooks
      const receivedRes = await axios.get(`${API}/admin/webhook-received`);
      setReceivedWebhooks(receivedRes.data || []);

      // Load cached blogs
      const blogsRes = await axios.get(`${API}/admin/blogs`);
      setCachedBlogs(blogsRes.data || []);

      // Load cron settings
      const cronRes = await axios.get(`${API}/admin/blogs/cron`);
      setCronSettings(cronRes.data || { enabled: false, interval_hours: 6, last_run: "" });

      // Load stats
      const statsRes = await axios.get(`${API}/admin/stats?days=${statsDays}`);
      setStats(statsRes.data || null);

      // Load SEO settings
      const seoRes = await axios.get(`${API}/admin/seo`);
      setSeoSettings(seoRes.data || null);

      // Load About settings
      const aboutRes = await axios.get(`${API}/admin/about`);
      setAboutSettings(aboutRes.data || null);

      // Load Portfolio
      const portfolioRes = await axios.get(`${API}/admin/portfolio`);
      setPortfolioProjects(portfolioRes.data || []);

      // Load Portal Projects
      const portalRes = await axios.get(`${API}/admin/projects`);
      setPortalProjects(portalRes.data || []);

      // Load Portal Webhook
      const webhookRes = await axios.get(`${API}/admin/portal-webhook`);
      setPortalWebhookUrl(webhookRes.data?.url || "");

      // Load Social Feed
      try {
        const socialRes = await axios.get(`${API}/social-feed`);
        setSocialPosts(socialRes.data || []);
      } catch (e) { console.error("Social feed load error:", e); }

      // Load Company Settings
      try {
        const companyRes = await axios.get(`${API}/company-settings`);
        setCompanySettings(companyRes.data || null);
      } catch (e) { console.error("Company settings load error:", e); }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/settings`, { webhook_url: webhookUrl });
      toast.success("Einstellungen gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API}/admin/company-settings`, companySettings);
      toast.success("Firmendaten gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern der Firmendaten");
    } finally {
      setSavingCompany(false);
    }
  };

  const useInternalWebhook = () => {
    setWebhookUrl(`${BACKEND_URL}/api/webhook/receive`);
    toast.info("Interne Test-URL eingesetzt. Klicken Sie 'Speichern' zum Aktivieren.");
  };

  const refreshBlogs = async () => {
    setRefreshingBlogs(true);
    try {
      const res = await axios.post(`${API}/admin/blogs/refresh`);
      toast.success(`${res.data.total} Artikel geladen (${res.data.new} neue)`);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setRefreshingBlogs(false);
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      await axios.delete(`${API}/admin/blogs/${blogId}`);
      toast.success("Artikel gelöscht");
      setCachedBlogs(cachedBlogs.filter(b => b.id !== blogId));
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const toggleBlog = async (blogId) => {
    try {
      const res = await axios.post(`${API}/admin/blogs/${blogId}/toggle`);
      setCachedBlogs(cachedBlogs.map(b => b.id === blogId ? { ...b, hidden: res.data.hidden } : b));
      toast.success(res.data.hidden ? "Artikel ausgeblendet" : "Artikel sichtbar");
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const saveCronSettings = async () => {
    try {
      await axios.post(`${API}/admin/blogs/cron`, cronSettings);
      toast.success("Cronjob-Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await axios.post(`${API}/admin/webhook-test`);
      if (res.data.log?.status === "success") {
        toast.success(`Test erfolgreich! Status: ${res.data.log.status_code}`);
      } else {
        toast.error(`Test fehlgeschlagen: ${res.data.log?.response_body?.substring(0, 100) || "Unbekannter Fehler"}`);
      }
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Testen");
    } finally {
      setTestingWebhook(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`);
      window.open(response.data.authorization_url, "_blank");
      toast.info("Bitte melden Sie sich im neuen Fenster an und kehren Sie dann zurück.");
    } catch (error) {
      toast.error("Fehler beim Verbinden");
    }
  };

  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const disconnectCalendar = async () => {
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/calendar/disconnect`);
      setCalendarStatus({ connected: false });
      setShowDisconnectConfirm(false);
      toast.success("Google-Verbindung wurde erfolgreich getrennt.");
    } catch (error) {
      toast.error("Fehler beim Trennen der Verbindung");
    } finally {
      setDisconnecting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-lab-bg-light flex items-center justify-center p-4">
        <div className="bg-white p-8 border border-lab-border max-w-md w-full">
          <h1 className="font-playfair text-2xl font-semibold text-lab-primary mb-6 text-center">
            LAB336 Admin
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort eingeben"
                className="rounded-none border-lab-border"
              />
            </div>
            <button
              type="submit"
              className="btn-primary rounded-none w-full"
            >
              Anmelden
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabGroups = [
    { label: "Kunden", items: [
      { id: "portal", label: "Portal", icon: Shield },
      { id: "kalkulator", label: "Kalkulator", icon: Calculator },
      { id: "roi", label: "ROI-Rechner", icon: TrendingUp },
      { id: "testimonials", label: "Testimonials", icon: MessageSquare },
      { id: "contacts", label: "Kontakte", icon: Mail },
      { id: "appointments", label: "Termine", icon: Clock },
      { id: "calendar", label: "Kalender", icon: Calendar },
    ]},
    { label: "Content", items: [
      { id: "blogs", label: "Blog", icon: FileText },
      { id: "portfolio", label: "Portfolio", icon: FolderOpen },
      { id: "social", label: "Social Feed", icon: ExternalLink },
      { id: "about", label: "Über mich", icon: UserCircle },
    ]},
    { label: "Marketing", items: [
      { id: "seo", label: "SEO", icon: Search },
      { id: "newsletter", label: "Newsletter", icon: Users },
      { id: "stats", label: "Statistiken", icon: BarChart3 },
    ]},
    { label: "System", items: [
      { id: "settings", label: "Einstellungen", icon: Settings },
    ]},
  ];

  return (
    <div className="min-h-screen bg-lab-bg-light">
      {/* Header */}
      <header className="bg-lab-primary text-white">
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-playfair text-xl font-semibold">LAB336</h1>
            <span className="text-lab-accent text-sm">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={loadData}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm hover:text-lab-accent transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-white border-r border-lab-border py-6 px-3" data-testid="admin-sidebar">
          {tabGroups.map((group) => (
            <div key={group.label} className="mb-5">
              <div className="px-3 mb-1.5 text-[0.65rem] font-bold uppercase text-lab-text-muted tracking-[0.15em]">
                {group.label}
              </div>
              {group.items.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors mb-0.5 ${
                      activeTab === tab.id
                        ? "bg-lab-primary text-white font-medium"
                        : "text-lab-text-muted hover:bg-lab-bg-light hover:text-lab-primary"
                    }`}
                    data-testid={`admin-tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <main className={`flex-1 ${(activeTab === 'kalkulator' || activeTab === 'roi') ? 'p-0' : 'p-8 max-w-5xl'}`}>
        {activeTab === "kalkulator" ? (
          <AngebotsKalkulator />
        ) : activeTab === "roi" ? (
          <div className="p-8 max-w-5xl">
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-6">ROI-Rechner</h2>
            <ROICalculator />
          </div>
        ) : activeTab === "testimonials" ? (
          <div className="p-8 max-w-5xl">
            <TestimonialManager />
          </div>
        ) : (
        <div className="bg-white border border-lab-border p-6">
          {/* Calendar Tab */}
          {activeTab === "calendar" && (
            <div className="space-y-6">
              <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                Google Calendar Verbindung
              </h2>
              
              <div className={`p-6 border ${
                calendarStatus.connected 
                  ? "border-green-200 bg-green-50" 
                  : "border-amber-200 bg-amber-50"
              }`}>
                <div className="flex items-center gap-4">
                  {calendarStatus.connected ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-amber-600" />
                  )}
                  <div>
                    <p className={`font-semibold ${
                      calendarStatus.connected ? "text-green-800" : "text-amber-800"
                    }`}>
                      {calendarStatus.connected ? "Verbunden" : "Nicht verbunden"}
                    </p>
                    {calendarStatus.email && (
                      <p className="text-green-600 text-sm">{calendarStatus.email}</p>
                    )}
                  </div>
                </div>
              </div>

              {!calendarStatus.connected ? (
                <button
                  onClick={connectCalendar}
                  className="btn-primary rounded-none flex items-center gap-2"
                  data-testid="connect-calendar-btn"
                >
                  <Link className="w-4 h-4" />
                  Google Calendar verbinden
                </button>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="bg-red-50 text-red-700 border border-red-200 px-4 py-2 text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
                    data-testid="disconnect-calendar-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Verbindung trennen
                  </button>

                  {showDisconnectConfirm && (
                    <div className="bg-red-50 border border-red-300 p-4 space-y-3">
                      <p className="text-sm font-semibold text-red-800">Sind Sie sicher?</p>
                      <p className="text-sm text-red-700">Folgendes wird deaktiviert bis ein neues Konto verbunden wird:</p>
                      <ul className="text-sm text-red-700 list-disc list-inside ml-2 space-y-1">
                        <li>Live-Kalender-Verfügbarkeit</li>
                        <li>Automatische Google Calendar-Einträge</li>
                        <li>Google Meet-Links bei Buchungen</li>
                        <li>Bestätigungs-E-Mails via Gmail</li>
                      </ul>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={disconnectCalendar}
                          disabled={disconnecting}
                          className="bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                          data-testid="confirm-disconnect-btn"
                        >
                          {disconnecting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
                          {disconnecting ? "Trennen..." : "Ja, trennen"}
                        </button>
                        <button
                          onClick={() => setShowDisconnectConfirm(false)}
                          className="bg-white border border-gray-300 text-gray-700 px-4 py-2 text-sm font-medium hover:bg-gray-50"
                          data-testid="cancel-disconnect-btn"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="text-sm text-lab-text-muted space-y-2">
                <p><strong>{calendarStatus.connected ? "Aktive Funktionen:" : "Wenn verbunden:"}</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Verfügbarkeit wird live aus Ihrem Kalender gelesen</li>
                  <li>Neue Termine werden automatisch eingetragen</li>
                  <li>Google Meet-Links werden automatisch erstellt</li>
                  <li>Bestätigungs-E-Mails werden automatisch versendet</li>
                </ul>
              </div>
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === "appointments" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                  Gebuchte Termine
                </h2>
                <span className="text-sm text-lab-text-muted">
                  {appointments.length} Termine
                </span>
              </div>
              
              {appointments.length === 0 ? (
                <p className="text-lab-text-muted py-8 text-center">
                  Noch keine Termine gebucht.
                </p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="bg-lab-bg-light border border-lab-border" data-testid={`appointment-${apt.id}`}>
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedAppointment(expandedAppointment === apt.id ? null : apt.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-lab-primary">{apt.first_name ? `${apt.first_name} ${apt.last_name || ''}`.trim() : apt.name}</p>
                            <p className="text-sm text-lab-text-muted">{apt.email}</p>
                            {apt.company && (
                              <p className="text-sm text-lab-text-muted flex items-center gap-1 mt-1">
                                <Building className="w-3 h-3" />
                                {apt.company}
                                {apt.company_size && <span className="text-xs">({apt.company_size})</span>}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex items-start gap-2">
                            <div>
                              <p className="font-semibold text-lab-accent">{apt.date}</p>
                              <p className="text-sm text-lab-text-muted">{apt.time_slot} Uhr</p>
                            </div>
                            {expandedAppointment === apt.id ? (
                              <ChevronUp className="w-4 h-4 text-lab-text-muted mt-1" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-lab-text-muted mt-1" />
                            )}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-lab-border flex items-center justify-between">
                          <span className="text-sm bg-lab-accent/20 text-lab-primary px-2 py-1">
                            {apt.topic}
                          </span>
                          {(apt.calendar_event_id || apt.atlas_calendar) ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Im Kalender
                            </span>
                          ) : (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const res = await axios.post(`${API}/admin/appointments/${apt.id}/add-to-calendar`);
                                  if (res.data.success) {
                                    toast.success("Kalendereintrag erstellt!");
                                    loadData();
                                  }
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Kalendereintrag fehlgeschlagen");
                                }
                              }}
                              className="text-xs text-lab-accent hover:text-lab-primary flex items-center gap-1 border border-lab-accent/30 px-2 py-0.5 hover:bg-lab-accent/5 transition-colors"
                              data-testid={`add-calendar-${apt.id}`}
                            >
                              <Calendar className="w-3 h-3" />
                              Zum Kalender
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedAppointment === apt.id && (
                        <div className="px-4 pb-4 border-t border-lab-border">
                          <div className="grid sm:grid-cols-2 gap-4 pt-4">
                            {/* Contact Details */}
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-lab-text-muted uppercase tracking-wider">Kontakt</p>
                              {apt.phone && (
                                <p className="text-sm flex items-center gap-2">
                                  <Phone className="w-3 h-3 text-lab-accent" />
                                  {apt.phone}
                                </p>
                              )}
                              {apt.position && (
                                <p className="text-sm flex items-center gap-2">
                                  <Briefcase className="w-3 h-3 text-lab-accent" />
                                  {apt.position}
                                </p>
                              )}
                              {apt.linkedin && (
                                <a href={apt.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-2 text-lab-accent hover:underline">
                                  <Linkedin className="w-3 h-3" />
                                  LinkedIn Profil
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>

                            {/* Company Details */}
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-lab-text-muted uppercase tracking-wider">Unternehmen</p>
                              {apt.industry && (
                                <p className="text-sm flex items-center gap-2">
                                  <Building className="w-3 h-3 text-lab-accent" />
                                  Branche: {apt.industry}
                                </p>
                              )}
                              {apt.website && (
                                <a href={apt.website} target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-2 text-lab-accent hover:underline">
                                  <Globe className="w-3 h-3" />
                                  {apt.website}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Questionnaire */}
                          {apt.questionnaire && Object.keys(apt.questionnaire).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-lab-border">
                              <p className="text-xs font-semibold text-lab-text-muted uppercase tracking-wider mb-3 flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                Fragebogen-Antworten
                              </p>
                              <div className="space-y-2">
                                {Object.entries(apt.questionnaire).map(([key, value]) => (
                                  <div key={key} className="bg-white p-3 border border-lab-border">
                                    <p className="text-xs text-lab-text-muted">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                    <p className="text-sm text-lab-primary mt-1">{value}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === "contacts" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                  Kontaktanfragen
                </h2>
                <span className="text-sm text-lab-text-muted">
                  {contacts.length} Anfragen
                </span>
              </div>
              
              {contacts.length === 0 ? (
                <p className="text-lab-text-muted py-8 text-center">
                  Noch keine Kontaktanfragen.
                </p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 bg-lab-bg-light border border-lab-border">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-lab-primary">{contact.first_name ? `${contact.first_name} ${contact.last_name || ''}`.trim() : contact.name}</p>
                          <a 
                            href={`mailto:${contact.email}`}
                            className="text-sm text-lab-accent hover:underline"
                          >
                            {contact.email}
                          </a>
                          {contact.company && (
                            <p className="text-sm text-lab-text-muted">{contact.company}</p>
                          )}
                        </div>
                        <span className="text-xs text-lab-text-muted">
                          {new Date(contact.timestamp).toLocaleDateString('de-CH')}
                        </span>
                      </div>
                      <p className="text-sm text-lab-text bg-white p-3 border border-lab-border">
                        {contact.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Blog Management Tab */}
          {activeTab === "blogs" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">Blog-Verwaltung</h2>
                <button
                  onClick={refreshBlogs}
                  disabled={refreshingBlogs}
                  className="btn-primary rounded-none flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
                  data-testid="refresh-blogs-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingBlogs ? "animate-spin" : ""}`} />
                  {refreshingBlogs ? "Laden..." : "Von Atlas laden"}
                </button>
              </div>

              {/* Cronjob Settings */}
              <div className="bg-lab-bg-light p-4 border border-lab-border space-y-3">
                <h3 className="font-semibold text-lab-primary text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4 text-lab-accent" />
                  Automatischer Abruf (Cronjob)
                </h3>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cronSettings.enabled}
                      onChange={(e) => setCronSettings({ ...cronSettings, enabled: e.target.checked })}
                      className="accent-lab-accent"
                    />
                    Aktiviert
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-lab-text-muted">Alle</span>
                    <select
                      value={cronSettings.interval_hours}
                      onChange={(e) => setCronSettings({ ...cronSettings, interval_hours: parseInt(e.target.value) })}
                      className="border border-lab-border rounded-none px-2 py-1 text-sm"
                    >
                      <option value={1}>1 Stunde</option>
                      <option value={3}>3 Stunden</option>
                      <option value={6}>6 Stunden</option>
                      <option value={12}>12 Stunden</option>
                      <option value={24}>24 Stunden</option>
                    </select>
                  </div>
                  <button onClick={saveCronSettings} className="text-sm text-lab-accent hover:underline flex items-center gap-1">
                    <Save className="w-3 h-3" /> Speichern
                  </button>
                </div>
                {cronSettings.last_run && (
                  <p className="text-xs text-lab-text-muted">Letzter Abruf: {new Date(cronSettings.last_run).toLocaleString("de-CH")}</p>
                )}
              </div>

              {/* Blog List */}
              {cachedBlogs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-lab-text-muted mb-4">Keine Artikel im Cache.</p>
                  <button onClick={refreshBlogs} className="text-lab-accent hover:underline text-sm">
                    Jetzt von Atlas laden
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cachedBlogs.map((blog) => (
                    <div key={blog.id} className={`p-4 border ${blog.hidden ? "bg-gray-100 border-gray-200 opacity-60" : "bg-lab-bg-light border-lab-border"}`} data-testid={`blog-admin-${blog.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs bg-lab-accent/20 text-lab-primary px-2 py-0.5">{blog.category}</span>
                            {blog.hidden && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5">Ausgeblendet</span>}
                          </div>
                          <h3 className="font-semibold text-lab-primary text-sm">{blog.title}</h3>
                          <p className="text-xs text-lab-text-muted mt-1">{blog.excerpt?.substring(0, 100)}...</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-lab-text-muted">
                            <span>{blog.read_time}</span>
                            {blog.word_count > 0 && <span>{blog.word_count} Wörter</span>}
                            {blog.cached_at && <span>Cached: {new Date(blog.cached_at).toLocaleString("de-CH")}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button onClick={() => toggleBlog(blog.id)} className="p-2 hover:bg-gray-200 transition-colors" title={blog.hidden ? "Anzeigen" : "Ausblenden"}>
                            {blog.hidden ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
                          </button>
                          <a href={`/blog/${blog.id}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-200 transition-colors">
                            <ExternalLink className="w-4 h-4 text-lab-accent" />
                          </a>
                          <button onClick={() => deleteBlog(blog.id)} className="p-2 hover:bg-red-100 transition-colors" title="Löschen">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === "stats" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">Statistiken</h2>
                <select
                  value={statsDays}
                  onChange={(e) => { setStatsDays(parseInt(e.target.value)); }}
                  className="border border-lab-border rounded-none px-3 py-1.5 text-sm"
                >
                  <option value={7}>Letzte 7 Tage</option>
                  <option value={30}>Letzte 30 Tage</option>
                  <option value={90}>Letzte 90 Tage</option>
                </select>
              </div>

              {!stats ? (
                <p className="text-lab-text-muted py-8 text-center">Lade Statistiken...</p>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <p className="text-xs text-lab-text-muted uppercase tracking-wider">Gesamt Aufrufe</p>
                      <p className="text-3xl font-playfair font-semibold text-lab-primary mt-1">{stats.total_views}</p>
                      <p className="text-xs text-lab-text-muted mt-1">in {statsDays} Tagen</p>
                    </div>
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <p className="text-xs text-lab-text-muted uppercase tracking-wider">Heute</p>
                      <p className="text-3xl font-playfair font-semibold text-lab-accent mt-1">{stats.today_views}</p>
                      <p className="text-xs text-lab-text-muted mt-1">Seitenaufrufe</p>
                    </div>
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <p className="text-xs text-lab-text-muted uppercase tracking-wider">Blog-Reads</p>
                      <p className="text-3xl font-playfair font-semibold text-lab-primary mt-1">
                        {stats.blog_reads?.reduce((sum, b) => sum + b.count, 0) || 0}
                      </p>
                      <p className="text-xs text-lab-text-muted mt-1">Artikel gelesen</p>
                    </div>
                  </div>

                  {/* Daily Views Chart */}
                  {stats.daily_views?.length > 0 && (
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <h3 className="font-semibold text-lab-primary text-sm mb-4 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-lab-accent" />
                        Seitenaufrufe pro Tag
                      </h3>
                      <div className="flex items-end gap-1 h-32">
                        {stats.daily_views.map((d) => {
                          const maxCount = Math.max(...stats.daily_views.map(x => x.count));
                          const height = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
                          return (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.count} Aufrufe`}>
                              <span className="text-xs text-lab-text-muted opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>
                              <div className="w-full bg-lab-accent/80 hover:bg-lab-accent transition-colors rounded-t" style={{ height: `${Math.max(height, 4)}%` }} />
                              {stats.daily_views.length <= 14 && (
                                <span className="text-[10px] text-lab-text-muted">{d.date.slice(5)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Section Views */}
                  {stats.section_views?.length > 0 && (
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <h3 className="font-semibold text-lab-primary text-sm mb-4">Beliebteste Bereiche</h3>
                      <div className="space-y-2">
                        {stats.section_views.map((s) => {
                          const maxCount = stats.section_views[0]?.count || 1;
                          const pct = (s.count / maxCount) * 100;
                          return (
                            <div key={s.section} className="flex items-center gap-3">
                              <span className="text-sm text-lab-primary w-32 truncate">{SECTION_LABELS[s.section] || s.section}</span>
                              <div className="flex-1 bg-gray-200 h-5 relative">
                                <div className="absolute inset-y-0 left-0 bg-lab-accent/70" style={{ width: `${pct}%` }} />
                                <span className="absolute right-2 top-0.5 text-xs text-lab-primary">{s.count}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Blog Reads */}
                  {stats.blog_reads?.length > 0 && (
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <h3 className="font-semibold text-lab-primary text-sm mb-4">Blog-Artikel Aufrufe</h3>
                      <div className="space-y-2">
                        {stats.blog_reads.map((b) => (
                          <div key={b.blog_id} className="flex items-center justify-between p-2 bg-white border border-lab-border">
                            <span className="text-sm text-lab-primary truncate flex-1">{b.blog_id}</span>
                            <span className="text-sm font-semibold text-lab-accent ml-4">{b.count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Top Pages */}
                  {stats.top_pages?.length > 0 && (
                    <div className="bg-lab-bg-light p-5 border border-lab-border">
                      <h3 className="font-semibold text-lab-primary text-sm mb-4">Top Seiten</h3>
                      <div className="space-y-2">
                        {stats.top_pages.map((p) => (
                          <div key={p.path} className="flex items-center justify-between p-2 bg-white border border-lab-border">
                            <span className="text-sm text-lab-primary">{p.path}</span>
                            <span className="text-sm font-semibold text-lab-accent">{p.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Newsletter Tab */}
          {activeTab === "newsletter" && (
            <>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                  Newsletter-Abonnenten
                </h2>
                <span className="text-sm text-lab-text-muted">
                  {newsletters.length} Abonnenten
                </span>
              </div>
              
              {newsletters.length === 0 ? (
                <p className="text-lab-text-muted py-8 text-center">
                  Noch keine Newsletter-Abonnenten.
                </p>
              ) : (
                <div className="space-y-2">
                  {newsletters.map((sub) => (
                    <div key={sub.id} className="p-3 bg-lab-bg-light border border-lab-border flex items-center justify-between" data-testid={`subscriber-${sub.id}`}>
                      {editingSubscriber === sub.id ? (
                        <form className="flex items-center gap-2 flex-1" onSubmit={async (e) => {
                          e.preventDefault();
                          try {
                            await axios.put(`${API}/admin/newsletter-subscribers/${sub.id}`, { email: editSubscriberEmail });
                            toast.success("E-Mail aktualisiert");
                            setEditingSubscriber(null);
                            loadNewsletters();
                          } catch (err) { console.error(err); toast.error("Fehler beim Speichern"); }
                        }}>
                          <input
                            type="email"
                            value={editSubscriberEmail}
                            onChange={(e) => setEditSubscriberEmail(e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-lab-border bg-white"
                            data-testid={`subscriber-edit-input-${sub.id}`}
                            autoFocus
                          />
                          <button type="submit" className="text-green-600 hover:text-green-800 p-1" data-testid={`subscriber-save-${sub.id}`}>
                            <Save className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => setEditingSubscriber(null)} className="text-lab-text-muted hover:text-lab-primary p-1">
                            <X className="w-4 h-4" />
                          </button>
                        </form>
                      ) : (
                        <>
                          <a href={`mailto:${sub.email}`} className="text-lab-primary hover:text-lab-accent">{sub.email}</a>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-lab-text-muted">{new Date(sub.timestamp).toLocaleDateString('de-CH')}</span>
                            <button onClick={() => { setEditingSubscriber(sub.id); setEditSubscriberEmail(sub.email); }} className="text-lab-text-muted hover:text-lab-accent p-1" data-testid={`subscriber-edit-${sub.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={async () => {
                              if (!window.confirm(`"${sub.email}" wirklich löschen?`)) return;
                              try {
                                await axios.delete(`${API}/admin/newsletter-subscribers/${sub.id}`);
                                toast.success("Abonnent gelöscht");
                                loadNewsletters();
                              } catch (err) { console.error(err); toast.error("Fehler beim Löschen"); }
                            }} className="text-lab-text-muted hover:text-red-500 p-1" data-testid={`subscriber-delete-${sub.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-lab-border pt-6">
              <NewsletterManager />
            </div>
            </>
          )}

          {/* SEO Tab */}
          {activeTab === "seo" && seoSettings && (
            <div className="space-y-8" data-testid="seo-tab">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                  SEO-Einstellungen
                </h2>
                <button
                  onClick={async () => {
                    setSavingSeo(true);
                    try {
                      await axios.post(`${API}/admin/seo`, seoSettings);
                      toast.success("SEO-Einstellungen gespeichert!");
                    } catch (e) { console.error(e); toast.error("Fehler beim Speichern"); }
                    setSavingSeo(false);
                  }}
                  disabled={savingSeo}
                  className="flex items-center gap-2 bg-lab-primary text-white px-4 py-2 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors disabled:opacity-50"
                  data-testid="seo-save-btn"
                >
                  <Save className="w-4 h-4" />
                  {savingSeo ? "Speichert..." : "Speichern"}
                </button>
              </div>

              {/* Meta Tags */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><Globe className="w-4 h-4" /> Meta-Tags</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Seitentitel</label>
                  <Input
                    value={seoSettings.title || ""}
                    onChange={e => setSeoSettings({...seoSettings, title: e.target.value})}
                    className="rounded-none"
                    placeholder="LAB336 – Beratung für..."
                    data-testid="seo-title-input"
                  />
                  <p className="text-xs text-lab-text-muted">{(seoSettings.title || "").length}/60 Zeichen (optimal: 50-60)</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Meta-Beschreibung</label>
                  <textarea
                    value={seoSettings.description || ""}
                    onChange={e => setSeoSettings({...seoSettings, description: e.target.value})}
                    className="w-full border border-lab-border p-3 text-sm resize-none h-20 focus:outline-none focus:border-lab-accent"
                    placeholder="Beschreibung für Suchmaschinen..."
                    data-testid="seo-description-input"
                  />
                  <p className="text-xs text-lab-text-muted">{(seoSettings.description || "").length}/160 Zeichen (optimal: 150-160)</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Keywords</label>
                  <Input
                    value={seoSettings.keywords || ""}
                    onChange={e => setSeoSettings({...seoSettings, keywords: e.target.value})}
                    className="rounded-none"
                    placeholder="Beratung, KI, Digitalisierung..."
                    data-testid="seo-keywords-input"
                  />
                </div>
              </div>

              {/* Social Media / OG */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Social Media & Branding</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Favicon URL</label>
                  <Input
                    value={seoSettings.favicon_url || ""}
                    onChange={e => setSeoSettings({...seoSettings, favicon_url: e.target.value})}
                    className="rounded-none"
                    placeholder="/favicon.svg oder https://..."
                    data-testid="seo-favicon-input"
                  />
                  <p className="text-xs text-lab-text-muted">SVG, PNG oder ICO. Leer = Standard LAB336-Favicon</p>
                  {seoSettings.favicon_url && (
                    <div className="flex items-center gap-3 mt-2 p-2 bg-lab-bg-light border border-lab-border">
                      <img src={seoSettings.favicon_url} alt="Favicon Vorschau" className="w-8 h-8 object-contain" />
                      <span className="text-xs text-lab-text-muted">Vorschau</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">OG-Bild URL</label>
                  <Input
                    value={seoSettings.og_image || ""}
                    onChange={e => setSeoSettings({...seoSettings, og_image: e.target.value})}
                    className="rounded-none"
                    placeholder="https://..."
                    data-testid="seo-og-image-input"
                  />
                  <p className="text-xs text-lab-text-muted">Vorschaubild beim Teilen auf LinkedIn, WhatsApp, etc. (optimal: 1200x630px)</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Canonical URL</label>
                  <Input
                    value={seoSettings.canonical_url || ""}
                    onChange={e => setSeoSettings({...seoSettings, canonical_url: e.target.value})}
                    className="rounded-none"
                    placeholder="https://lab336.ch"
                    data-testid="seo-canonical-input"
                  />
                </div>
                <div className="border-t border-lab-border pt-4 mt-2">
                  <p className="text-sm font-medium text-lab-text mb-3">Social-Media-Profile</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs text-lab-text-muted">LinkedIn</label>
                      <Input value={seoSettings.social_linkedin || ""} onChange={e => setSeoSettings({...seoSettings, social_linkedin: e.target.value})} className="rounded-none" placeholder="https://linkedin.com/in/..." data-testid="seo-social-linkedin" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-lab-text-muted">Twitter / X</label>
                      <Input value={seoSettings.social_twitter || ""} onChange={e => setSeoSettings({...seoSettings, social_twitter: e.target.value})} className="rounded-none" placeholder="https://x.com/..." data-testid="seo-social-twitter" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-lab-text-muted">Facebook</label>
                      <Input value={seoSettings.social_facebook || ""} onChange={e => setSeoSettings({...seoSettings, social_facebook: e.target.value})} className="rounded-none" placeholder="https://facebook.com/..." data-testid="seo-social-facebook" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-lab-text-muted">Instagram</label>
                      <Input value={seoSettings.social_instagram || ""} onChange={e => setSeoSettings({...seoSettings, social_instagram: e.target.value})} className="rounded-none" placeholder="https://instagram.com/..." data-testid="seo-social-instagram" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Data for Structured Data */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><Building className="w-4 h-4" /> Unternehmensdaten (für Google Rich Snippets)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Firmenname</label>
                    <Input value={seoSettings.company_name || ""} onChange={e => setSeoSettings({...seoSettings, company_name: e.target.value})} className="rounded-none" data-testid="seo-company-name" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Strasse</label>
                    <Input value={seoSettings.company_street || ""} onChange={e => setSeoSettings({...seoSettings, company_street: e.target.value})} className="rounded-none" data-testid="seo-company-street" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">PLZ</label>
                    <Input value={seoSettings.company_zip || ""} onChange={e => setSeoSettings({...seoSettings, company_zip: e.target.value})} className="rounded-none" data-testid="seo-company-zip" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Ort</label>
                    <Input value={seoSettings.company_city || ""} onChange={e => setSeoSettings({...seoSettings, company_city: e.target.value})} className="rounded-none" data-testid="seo-company-city" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Telefon</label>
                    <Input value={seoSettings.company_phone || ""} onChange={e => setSeoSettings({...seoSettings, company_phone: e.target.value})} className="rounded-none" data-testid="seo-company-phone" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">E-Mail</label>
                    <Input value={seoSettings.company_email || ""} onChange={e => setSeoSettings({...seoSettings, company_email: e.target.value})} className="rounded-none" data-testid="seo-company-email" />
                  </div>
                </div>
              </div>

              {/* SEO Preview */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><Eye className="w-4 h-4" /> Google-Vorschau</h3>
                <div className="border border-gray-200 rounded-lg p-4 max-w-xl">
                  <p className="text-blue-700 text-lg leading-tight hover:underline cursor-pointer">{seoSettings.title || "Seitentitel"}</p>
                  <p className="text-green-700 text-sm mt-1">{seoSettings.canonical_url || "https://lab336.ch"}</p>
                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">{seoSettings.description || "Meta-Beschreibung..."}</p>
                </div>
              </div>

              {/* Info */}
              <div className="bg-lab-bg-light border border-lab-border p-4 text-sm text-lab-text-muted space-y-2">
                <p><strong>Automatisch aktiv:</strong></p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Favicon — Einstellbar über Feld oben (Standard: LAB336 "L")</li>
                  <li>Sprache — <code>lang="de"</code> gesetzt</li>
                  <li>robots.txt — /robots.txt</li>
                  <li>Sitemap — /api/sitemap.xml (dynamisch generiert inkl. Blog-Posts)</li>
                  <li>Strukturierte Daten (JSON-LD) — ProfessionalService Schema</li>
                  <li>Open Graph & Twitter Card Tags</li>
                  <li>Lazy Loading — Alle Bilder werden erst beim Scrollen geladen</li>
                  <li>404-Fehlerseite — Elegante Seite für nicht existierende URLs</li>
                  <li>H1-Hierarchie — Korrekt (1x H1 pro Seite)</li>
                </ul>
              </div>
            </div>
          )}

          {/* About Tab */}
          {activeTab === "about" && aboutSettings && (
            <div className="space-y-8" data-testid="about-tab">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                  Über mich — Bearbeiten
                </h2>
                <button
                  onClick={async () => {
                    setSavingAbout(true);
                    try {
                      await axios.post(`${API}/admin/about`, aboutSettings);
                      toast.success("Über-mich-Bereich gespeichert!");
                    } catch (e) { console.error(e); toast.error("Fehler beim Speichern"); }
                    setSavingAbout(false);
                  }}
                  disabled={savingAbout}
                  className="flex items-center gap-2 bg-lab-primary text-white px-4 py-2 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors disabled:opacity-50"
                  data-testid="about-save-btn"
                >
                  <Save className="w-4 h-4" />
                  {savingAbout ? "Speichert..." : "Speichern"}
                </button>
              </div>

              {/* Personal Info */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><UserCircle className="w-4 h-4" /> Persönliche Daten</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Name</label>
                    <Input value={aboutSettings.name || ""} onChange={e => setAboutSettings({...aboutSettings, name: e.target.value})} className="rounded-none" data-testid="about-name" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Titel / Rolle</label>
                    <Input value={aboutSettings.title || ""} onChange={e => setAboutSettings({...aboutSettings, title: e.target.value})} className="rounded-none" placeholder="z.B. Gründer & Berater" data-testid="about-title" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Foto URL</label>
                  <Input value={aboutSettings.image_url || ""} onChange={e => setAboutSettings({...aboutSettings, image_url: e.target.value})} className="rounded-none" placeholder="https://..." data-testid="about-image" />
                  {aboutSettings.image_url && (
                    <div className="mt-2 w-20 h-20 overflow-hidden bg-lab-bg-light border border-lab-border">
                      <img src={aboutSettings.image_url} alt="Vorschau" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              {/* Text */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><FileText className="w-4 h-4" /> Beschreibung</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Absatz 1</label>
                  <textarea
                    value={aboutSettings.paragraph_1 || ""}
                    onChange={e => setAboutSettings({...aboutSettings, paragraph_1: e.target.value})}
                    className="w-full border border-lab-border p-3 text-sm resize-none h-24 focus:outline-none focus:border-lab-accent"
                    data-testid="about-p1"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Absatz 2</label>
                  <textarea
                    value={aboutSettings.paragraph_2 || ""}
                    onChange={e => setAboutSettings({...aboutSettings, paragraph_2: e.target.value})}
                    className="w-full border border-lab-border p-3 text-sm resize-none h-24 focus:outline-none focus:border-lab-accent"
                    data-testid="about-p2"
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Kennzahlen</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Wert 1</label>
                    <Input value={aboutSettings.stat_1_value || ""} onChange={e => setAboutSettings({...aboutSettings, stat_1_value: e.target.value})} className="rounded-none" data-testid="about-stat1-val" />
                    <Input value={aboutSettings.stat_1_label || ""} onChange={e => setAboutSettings({...aboutSettings, stat_1_label: e.target.value})} className="rounded-none" placeholder="Label" data-testid="about-stat1-lbl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Wert 2</label>
                    <Input value={aboutSettings.stat_2_value || ""} onChange={e => setAboutSettings({...aboutSettings, stat_2_value: e.target.value})} className="rounded-none" data-testid="about-stat2-val" />
                    <Input value={aboutSettings.stat_2_label || ""} onChange={e => setAboutSettings({...aboutSettings, stat_2_label: e.target.value})} className="rounded-none" placeholder="Label" data-testid="about-stat2-lbl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">Wert 3</label>
                    <Input value={aboutSettings.stat_3_value || ""} onChange={e => setAboutSettings({...aboutSettings, stat_3_value: e.target.value})} className="rounded-none" data-testid="about-stat3-val" />
                    <Input value={aboutSettings.stat_3_label || ""} onChange={e => setAboutSettings({...aboutSettings, stat_3_label: e.target.value})} className="rounded-none" placeholder="Label" data-testid="about-stat3-lbl" />
                  </div>
                </div>
              </div>

              {/* Links */}
              <div className="bg-white border border-lab-border p-6 space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2"><Link className="w-4 h-4" /> Links & CTA</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">LinkedIn URL</label>
                    <Input value={aboutSettings.linkedin_url || ""} onChange={e => setAboutSettings({...aboutSettings, linkedin_url: e.target.value})} className="rounded-none" data-testid="about-linkedin" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-lab-text">E-Mail</label>
                    <Input value={aboutSettings.email || ""} onChange={e => setAboutSettings({...aboutSettings, email: e.target.value})} className="rounded-none" data-testid="about-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-lab-text">Button-Text</label>
                  <Input value={aboutSettings.cta_text || ""} onChange={e => setAboutSettings({...aboutSettings, cta_text: e.target.value})} className="rounded-none" placeholder="Gespräch vereinbaren" data-testid="about-cta-text" />
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Tab */}
          {activeTab === "portfolio" && (
            <div className="space-y-6" data-testid="portfolio-tab">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">Portfolio-Projekte</h2>
                <button
                  onClick={() => setEditingProject({
                    id: "", title: "", description: "", category: "web-app", type: "software",
                    year: new Date().getFullYear().toString(), image: "", images: [], technologies: [], link: "", order: portfolioProjects.length
                  })}
                  className="flex items-center gap-2 bg-lab-primary text-white px-4 py-2 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors"
                  data-testid="portfolio-add-btn"
                >
                  <Plus className="w-4 h-4" /> Neues Projekt
                </button>
              </div>

              {/* Project List */}
              <div className="space-y-3">
                {portfolioProjects.map((proj) => (
                  <div key={proj.id} className="bg-white border border-lab-border p-4 flex items-center gap-4" data-testid={`portfolio-item-${proj.id}`}>
                    <div className="w-16 h-12 overflow-hidden bg-lab-bg-light flex-shrink-0">
                      {proj.image && <img src={proj.image} alt={proj.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-lab-primary truncate">{proj.title}</h3>
                        <span className={`text-xs px-2 py-0.5 ${proj.type === "software" ? "bg-lab-primary text-white" : "bg-lab-accent text-lab-primary"}`}>
                          {proj.type === "software" ? "Software" : "Projekt"}
                        </span>
                      </div>
                      <p className="text-sm text-lab-text-muted truncate">{proj.description}</p>
                      <div className="flex gap-1 mt-1">
                        {(proj.images || []).length > 0 && (
                          <span className="text-xs text-lab-text-muted flex items-center gap-1"><Image className="w-3 h-3" /> {proj.images.length} Bild{proj.images.length !== 1 ? "er" : ""}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => setEditingProject({...proj})} className="p-2 border border-lab-border hover:border-lab-accent transition-colors" data-testid={`portfolio-edit-${proj.id}`}>
                        <Pencil className="w-4 h-4 text-lab-text-muted" />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`"${proj.title}" wirklich löschen?`)) return;
                          await axios.delete(`${API}/admin/portfolio/${proj.id}`);
                          setPortfolioProjects(prev => prev.filter(p => p.id !== proj.id));
                          toast.success("Projekt gelöscht");
                        }}
                        className="p-2 border border-lab-border hover:border-red-400 transition-colors"
                        data-testid={`portfolio-delete-${proj.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Edit/Create Modal */}
              {editingProject && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" data-testid="portfolio-edit-modal">
                  <div className="absolute inset-0 bg-lab-primary/50 backdrop-blur-sm" onClick={() => setEditingProject(null)} />
                  <div className="relative bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl p-6 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-playfair text-lg font-semibold text-lab-primary">
                        {editingProject.id ? "Projekt bearbeiten" : "Neues Projekt"}
                      </h3>
                      <button onClick={() => setEditingProject(null)} className="p-1 hover:bg-lab-bg-light"><X className="w-5 h-5" /></button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-lab-text">Titel</label>
                        <Input value={editingProject.title} onChange={e => setEditingProject({...editingProject, title: e.target.value})} className="rounded-none" data-testid="proj-title" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-lab-text">Jahr</label>
                        <Input value={editingProject.year} onChange={e => setEditingProject({...editingProject, year: e.target.value})} className="rounded-none" data-testid="proj-year" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-lab-text">Beschreibung</label>
                      <textarea
                        value={editingProject.description}
                        onChange={e => setEditingProject({...editingProject, description: e.target.value})}
                        className="w-full border border-lab-border p-3 text-sm resize-none h-20 focus:outline-none focus:border-lab-accent"
                        data-testid="proj-desc"
                      />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-lab-text">Kategorie</label>
                        <select value={editingProject.category} onChange={e => setEditingProject({...editingProject, category: e.target.value})} className="w-full border border-lab-border p-2 text-sm" data-testid="proj-category">
                          <option value="web-app">Web App</option>
                          <option value="tool">Tool</option>
                          <option value="automation">Automatisierung</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-lab-text">Typ</label>
                        <select value={editingProject.type} onChange={e => setEditingProject({...editingProject, type: e.target.value})} className="w-full border border-lab-border p-2 text-sm" data-testid="proj-type">
                          <option value="software">Software</option>
                          <option value="projekt">Projekt</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-lab-text">Reihenfolge</label>
                        <Input type="number" value={editingProject.order} onChange={e => setEditingProject({...editingProject, order: parseInt(e.target.value) || 0})} className="rounded-none" data-testid="proj-order" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-lab-text">Link (leer = kein Link)</label>
                      <Input value={editingProject.link || ""} onChange={e => setEditingProject({...editingProject, link: e.target.value})} className="rounded-none" placeholder="https://..." data-testid="proj-link" />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-lab-text">Technologien</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(editingProject.technologies || []).map((tech, i) => (
                          <span key={`${tech}-${i}`} className="flex items-center gap-1 text-sm bg-lab-bg-light text-lab-text px-2 py-1 border border-lab-border">
                            {tech}
                            <button onClick={() => setEditingProject({...editingProject, technologies: (editingProject.technologies || []).filter((_, j) => j !== i)})} className="text-lab-text-muted hover:text-red-400 ml-1"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                      <Input
                        className="rounded-none"
                        placeholder="Technologie eingeben + Enter"
                        data-testid="proj-tech"
                        onKeyDown={e => {
                          if (e.key === "Enter" && e.target.value.trim()) {
                            e.preventDefault();
                            setEditingProject({...editingProject, technologies: [...(editingProject.technologies || []), e.target.value.trim()]});
                            e.target.value = "";
                          }
                        }}
                      />
                    </div>

                    {/* Images */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-lab-text flex items-center gap-2"><Image className="w-4 h-4" /> Bilder</label>
                      {(editingProject.images || []).map((img, i) => {
                        const isCover = (editingProject.cover_index ?? 0) === i || (!editingProject.cover_index && editingProject.image === img);
                        return (
                        <div key={`${img}-${i}`} className={`flex items-center gap-2 p-1 ${isCover ? "border border-[#D4A373] bg-[#D4A373]/5" : ""}`}>
                          <div className="w-12 h-9 overflow-hidden bg-lab-bg-light flex-shrink-0 border border-lab-border relative">
                            {img && <img src={img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} alt="" className="w-full h-full object-cover" />}
                            {isCover && <span className="absolute bottom-0 left-0 right-0 bg-[#D4A373] text-white text-[8px] text-center font-semibold leading-tight">Cover</span>}
                          </div>
                          <span className="text-xs text-lab-text-muted truncate flex-1">{img.split("/").pop()}</span>
                          {!isCover && (
                            <button
                              onClick={() => setEditingProject({...editingProject, image: img, cover_index: i})}
                              className="p-1.5 text-lab-text-muted hover:text-[#D4A373] hover:bg-[#D4A373]/5 text-[10px] border border-lab-border"
                              title="Als Startbild setzen"
                            >
                              Cover
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const imgs = (editingProject.images || []).filter((_, j) => j !== i);
                              const newCoverIdx = isCover ? 0 : (editingProject.cover_index > i ? editingProject.cover_index - 1 : editingProject.cover_index);
                              setEditingProject({...editingProject, images: imgs, image: imgs[0] || "", cover_index: newCoverIdx || 0});
                            }}
                            className="p-2 text-red-400 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        );
                      })}
                      <label className="inline-flex items-center gap-2 text-sm text-lab-accent hover:text-lab-primary cursor-pointer" data-testid="proj-upload-img">
                        <Plus className="w-4 h-4" /> Bild hochladen
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                              toast.info("Bild wird hochgeladen...");
                              const res = await axios.post(`${API}/upload`, formData);
                              const url = res.data.url;
                              const imgs = [...(editingProject.images || []), url];
                              setEditingProject({...editingProject, images: imgs, image: imgs[0] || ""});
                              toast.success("Bild hochgeladen!");
                            } catch (e) {
                              console.error("Image upload error:", e);
                              toast.error("Upload fehlgeschlagen");
                            }
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <p className="text-xs text-lab-text-muted">Oder Bild-URL direkt einfügen:</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://... oder /pfad/bild.png"
                          className="rounded-none flex-1"
                          data-testid="proj-img-url"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && e.target.value.trim()) {
                              const imgs = [...(editingProject.images || []), e.target.value.trim()];
                              setEditingProject({...editingProject, images: imgs, image: imgs[0] || ""});
                              e.target.value = "";
                            }
                          }}
                        />
                        <button
                          onClick={(e) => {
                            const input = e.target.closest("div").querySelector("input");
                            if (input?.value.trim()) {
                              const imgs = [...(editingProject.images || []), input.value.trim()];
                              setEditingProject({...editingProject, images: imgs, image: imgs[0] || ""});
                              input.value = "";
                            }
                          }}
                          className="px-3 border border-lab-border text-sm hover:bg-lab-bg-light"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Save */}
                    <div className="flex gap-3 pt-4 border-t border-lab-border">
                      <button
                        onClick={async () => {
                          setSavingProject(true);
                          try {
                            const data = {...editingProject};
                            if (!data.image && data.images?.length) data.image = data.images[0];
                            if (editingProject.id && portfolioProjects.find(p => p.id === editingProject.id)) {
                              await axios.put(`${API}/admin/portfolio/${editingProject.id}`, data);
                            } else {
                              const res = await axios.post(`${API}/admin/portfolio`, data);
                              data.id = res.data.id;
                            }
                            const refreshed = await axios.get(`${API}/admin/portfolio`);
                            setPortfolioProjects(refreshed.data);
                            setEditingProject(null);
                            toast.success("Projekt gespeichert!");
                          } catch (e) { console.error(e); toast.error("Fehler beim Speichern"); }
                          setSavingProject(false);
                        }}
                        disabled={savingProject}
                        className="flex items-center gap-2 bg-lab-primary text-white px-5 py-2 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors disabled:opacity-50"
                        data-testid="proj-save-btn"
                      >
                        <Save className="w-4 h-4" /> {savingProject ? "Speichert..." : "Speichern"}
                      </button>
                      <button onClick={() => setEditingProject(null)} className="px-5 py-2 text-sm border border-lab-border hover:bg-lab-bg-light">Abbrechen</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Social Feed Tab */}
          {activeTab === "social" && (
            <div className="space-y-6" data-testid="social-feed-tab">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">Social Media Beiträge</h2>
                <span className="text-xs text-lab-text-muted">{socialPosts.length} Beiträge</span>
              </div>

              {socialPosts.length === 0 ? (
                <div className="bg-white border border-lab-border p-12 text-center">
                  <p className="text-lab-text-muted text-sm">Keine Social-Media-Beiträge vorhanden.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {socialPosts.map(post => (
                    <div key={post.id} className="bg-white border border-lab-border p-4" data-testid={`social-post-${post.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[0.6rem] font-bold uppercase px-2 py-0.5 ${
                              post.platform === "linkedin" ? "bg-blue-50 text-blue-700" :
                              post.platform === "twitter" ? "bg-sky-50 text-sky-700" :
                              post.platform === "instagram" ? "bg-pink-50 text-pink-700" :
                              "bg-gray-50 text-gray-700"
                            }`} style={{letterSpacing:"0.1em"}}>{post.platform || "Social"}</span>
                            {post.author && <span className="text-xs text-lab-text-muted">{post.author}</span>}
                            {post.published_at && <span className="text-xs text-lab-text-muted">{new Date(post.published_at).toLocaleDateString("de-CH")}</span>}
                            {post.visible === false && <span className="text-[0.6rem] font-bold uppercase px-2 py-0.5 bg-red-50 text-red-600" style={{letterSpacing:"0.1em"}}>Versteckt</span>}
                          </div>
                          {post.content && <p className="text-sm text-lab-primary line-clamp-3">{post.content}</p>}
                          {post.url && (
                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-xs text-lab-accent hover:underline mt-1 inline-block">
                              Originalbeitrag öffnen →
                            </a>
                          )}
                        </div>
                        {post.image && (
                          <img src={post.image} alt="" className="w-16 h-16 object-cover border border-lab-border flex-shrink-0" />
                        )}
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button
                            onClick={async () => {
                              const newVisible = post.visible === false ? true : false;
                              try {
                                await axios.put(`${API}/admin/social-feed/${post.id}`, { visible: newVisible });
                                setSocialPosts(prev => prev.map(p => p.id === post.id ? {...p, visible: newVisible} : p));
                                toast.success(newVisible ? "Beitrag sichtbar" : "Beitrag versteckt");
                              } catch (e) { console.error(e); toast.error("Fehler"); }
                            }}
                            className="text-xs text-lab-text-muted hover:text-lab-primary px-2 py-1 border border-lab-border hover:bg-lab-bg-light transition-colors"
                            data-testid={`social-toggle-${post.id}`}
                          >
                            {post.visible === false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm("Beitrag dauerhaft löschen?")) return;
                              try {
                                await axios.delete(`${API}/admin/social-feed/${post.id}`);
                                setSocialPosts(prev => prev.filter(p => p.id !== post.id));
                                toast.success("Beitrag gelöscht");
                              } catch (e) { console.error(e); toast.error("Fehler beim Löschen"); }
                            }}
                            className="text-xs text-red-500 hover:text-red-700 px-2 py-1 border border-red-200 hover:bg-red-50 transition-colors"
                            data-testid={`social-delete-${post.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Portal Tab */}
          {activeTab === "portal" && (
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="font-playfair text-xl font-semibold text-lab-primary">Kundenportal</h2>
              </div>

              {/* Webhook config */}
              <div className="bg-white p-5 border border-lab-border">
                <div className="flex items-center gap-2 mb-3">
                  <Webhook className="w-4 h-4 text-lab-accent" />
                  <h3 className="text-sm font-semibold text-lab-primary">Atlas OS Webhook</h3>
                </div>
                <p className="text-xs text-lab-text-muted mb-3">Bei jeder Formular-Einreichung wird ein strukturierter POST-Request an diese URL gesendet.</p>
                <div className="flex gap-2">
                  <Input
                    value={portalWebhookUrl}
                    onChange={e => setPortalWebhookUrl(e.target.value)}
                    className="rounded-none flex-1"
                    placeholder="https://..."
                    data-testid="portal-webhook-url"
                  />
                  <button
                    onClick={async () => {
                      await axios.post(`${API}/admin/portal-webhook`, { url: portalWebhookUrl });
                      toast.success("Webhook gespeichert");
                    }}
                    className="btn-primary rounded-none px-4 text-sm"
                    data-testid="portal-webhook-save"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Create new project */}
              <div className="bg-white p-6 border border-lab-border space-y-4">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2">
                  <Plus className="w-4 h-4 text-lab-accent" /> Neues Projekt erstellen
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs text-lab-text-muted">Kundenname *</label>
                    <Input value={newProject.client_name} onChange={e => setNewProject({...newProject, client_name: e.target.value})} className="rounded-none" placeholder="Firma / Kunde" data-testid="portal-new-name" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-lab-text-muted">E-Mail</label>
                    <Input value={newProject.client_email} onChange={e => setNewProject({...newProject, client_email: e.target.value})} className="rounded-none" placeholder="kunde@firma.ch" data-testid="portal-new-email" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-lab-text-muted">Passwort *</label>
                    <Input value={newProject.password} onChange={e => setNewProject({...newProject, password: e.target.value})} className="rounded-none" placeholder="Zugangspasswort" data-testid="portal-new-password" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-lab-text-muted">Notizen</label>
                    <Input value={newProject.notes} onChange={e => setNewProject({...newProject, notes: e.target.value})} className="rounded-none" placeholder="Interne Notizen" data-testid="portal-new-notes" />
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (!newProject.client_name || !newProject.password) { toast.error("Name und Passwort sind Pflichtfelder"); return; }
                    setCreatingProject(true);
                    try {
                      const res = await axios.post(`${API}/admin/projects`, newProject);
                      setPortalProjects(prev => [res.data.project, ...prev]);
                      setNewProject({ client_name: "", client_email: "", password: "", notes: "" });
                      toast.success("Projekt erstellt!");
                    } catch (e) { console.error(e); toast.error("Fehler beim Erstellen"); }
                    finally { setCreatingProject(false); }
                  }}
                  disabled={creatingProject}
                  className="btn-primary rounded-none px-5 py-2 text-sm flex items-center gap-2"
                  data-testid="portal-create-btn"
                >
                  <Plus className="w-4 h-4" /> Erstellen
                </button>
              </div>

              {/* Project list */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lab-primary">{portalProjects.length} Projekte</h3>
                {portalProjects.map(project => (
                  <div key={project.id} className="bg-white border border-lab-border">
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-semibold text-lab-primary">{project.client_name}</span>
                          <span className={`text-xs px-2 py-0.5 ${project.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {project.status === "active" ? "Aktiv" : "Inaktiv"}
                          </span>
                          {project.submission_count > 0 && (
                            <span className="text-xs bg-lab-accent/20 text-lab-primary px-2 py-0.5">{project.submission_count} Antwort(en)</span>
                          )}
                          {project.file_count > 0 && (
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5">{project.file_count} Datei(en)</span>
                          )}
                          {(() => {
                            const ps = PROJECT_STATUSES.find(s => s.value === (project.project_status || "kickoff_pending"));
                            return ps ? <span className={`text-xs px-2 py-0.5 ${ps.color}`}>{ps.label}</span> : null;
                          })()}
                        </div>
                        <p className="text-xs text-lab-text-muted">
                          {project.client_email && `${project.client_email} · `}
                          Erstellt: {new Date(project.created_at).toLocaleDateString("de-CH")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/portal/${project.id}`;
                            navigator.clipboard.writeText(url);
                            toast.success("Portal-Link kopiert!");
                          }}
                          className="text-xs bg-lab-bg-light border border-lab-border px-3 py-1.5 hover:bg-lab-accent/10 transition-colors flex items-center gap-1"
                          data-testid={`portal-copy-${project.id}`}
                        >
                          <Copy className="w-3 h-3" /> Link
                        </button>
                        <button
                          onClick={() => window.open(`/portal/${project.id}`, '_blank')}
                          className="text-xs bg-lab-accent/10 border border-lab-accent/30 text-lab-accent px-3 py-1.5 hover:bg-lab-accent/20 transition-colors flex items-center gap-1"
                          data-testid={`portal-open-${project.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Portal
                        </button>
                        <button
                          onClick={async () => {
                            const pid = project.id;
                            if (loadingDocs === pid) return;
                            if (projectDocs[pid]) {
                              setProjectDocs(prev => { const n = {...prev}; delete n[pid]; return n; });
                              return;
                            }
                            setLoadingDocs(pid);
                            try {
                              const res = await axios.get(`${API}/admin/projects/${pid}/documents`);
                              setProjectDocs(prev => ({...prev, [pid]: res.data}));
                            } catch (e) { console.error(e); toast.error("Dokumente konnten nicht geladen werden"); }
                            finally { setLoadingDocs(null); }
                          }}
                          className="text-xs bg-lab-bg-light border border-lab-border px-3 py-1.5 hover:bg-lab-accent/10 transition-colors flex items-center gap-1"
                          data-testid={`portal-docs-${project.id}`}
                        >
                          <FileText className="w-3 h-3" /> {projectDocs[project.id] ? "Dokumente ▲" : "Dokumente ▼"}
                        </button>
                        <button
                          onClick={async () => {
                            if (viewingProject === project.id) {
                              setViewingProject(null);
                              setProjectDetail(null);
                            } else {
                              const res = await axios.get(`${API}/admin/projects/${project.id}`);
                              setProjectDetail(res.data);
                              setViewingProject(project.id);
                            }
                          }}
                          className="text-xs bg-lab-bg-light border border-lab-border px-3 py-1.5 hover:bg-lab-accent/10 transition-colors flex items-center gap-1"
                          data-testid={`portal-view-${project.id}`}
                        >
                          <Eye className="w-3 h-3" /> Briefing
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Projekt und alle Daten löschen?")) return;
                            await axios.delete(`${API}/admin/projects/${project.id}`);
                            setPortalProjects(prev => prev.filter(p => p.id !== project.id));
                            toast.success("Projekt gelöscht");
                          }}
                          className="text-xs text-red-600 hover:bg-red-50 px-2 py-1.5 transition-colors"
                          data-testid={`portal-delete-${project.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Documents View */}
                    {projectDocs[project.id] && (
                      <div className="border-t border-lab-border p-5 bg-[#FAFAF8]" data-testid={`portal-docs-view-${project.id}`}>
                        <div className="space-y-4">
                          {/* Offers */}
                          <div>
                            <h4 className="text-xs font-bold uppercase text-lab-text-muted mb-2" style={{letterSpacing:"0.1em"}}>
                              Angebote ({projectDocs[project.id].offers?.length || 0})
                            </h4>
                            {(!projectDocs[project.id].offers || projectDocs[project.id].offers.length === 0) ? (
                              <p className="text-xs text-lab-text-muted italic">Keine Angebote vorhanden</p>
                            ) : (
                              <div className="space-y-2">
                                {projectDocs[project.id].offers.map(offer => (
                                  <div key={offer.offer_number} className="bg-white border border-lab-border p-3 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                      <FileText className="w-4 h-4 text-lab-accent flex-shrink-0" />
                                      <div className="min-w-0">
                                        <span className="text-sm font-semibold text-lab-primary block">{offer.offer_number}</span>
                                        {offer.project_name && <span className="text-xs text-lab-text-muted">{offer.project_name}</span>}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      {(offer.amount_chf || offer.endpreis_chf) && (
                                        <span className="text-sm font-semibold text-lab-primary">CHF {Number(offer.amount_chf || offer.endpreis_chf).toLocaleString("de-CH", {minimumFractionDigits:2})}</span>
                                      )}
                                      <span className={`text-[0.65rem] font-bold uppercase px-2 py-0.5 ${
                                        offer.portal_status === "accepted" ? "bg-green-50 text-green-700" :
                                        offer.portal_status === "rejected" ? "bg-red-50 text-red-600" :
                                        "bg-amber-50 text-amber-700"
                                      }`}>
                                        {offer.portal_status === "accepted" ? "Akzeptiert" : offer.portal_status === "rejected" ? "Abgelehnt" : "Ausstehend"}
                                      </span>
                                      {offer.pdf_url && (
                                        <a href={offer.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-lab-accent hover:underline flex items-center gap-1">
                                          <ExternalLink className="w-3 h-3" /> PDF
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Invoices */}
                          {projectDocs[project.id].invoices?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold uppercase text-lab-text-muted mb-2" style={{letterSpacing:"0.1em"}}>
                                Rechnungen ({projectDocs[project.id].invoices.length})
                              </h4>
                              <div className="space-y-2">
                                {projectDocs[project.id].invoices.map((inv, i) => (
                                  <div key={inv.invoice_number || i} className="bg-white border border-lab-border p-3 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <FileText className="w-4 h-4 text-[#64748B] flex-shrink-0" />
                                      <div>
                                        <span className="text-sm font-semibold text-lab-primary">{inv.invoice_number || `Rechnung ${i+1}`}</span>
                                        {inv.label && <span className="text-xs text-lab-text-muted ml-2">{inv.label}</span>}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      {inv.amount_chf && (
                                        <span className="text-sm font-semibold text-lab-primary">CHF {Number(inv.amount_chf).toLocaleString("de-CH", {minimumFractionDigits:2})}</span>
                                      )}
                                      <span className={`text-[0.65rem] font-bold uppercase px-2 py-0.5 ${
                                        inv.status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                                      }`}>
                                        {inv.status === "paid" ? "Bezahlt" : inv.status || "Offen"}
                                      </span>
                                      {inv.pdf_url && (
                                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-lab-accent hover:underline flex items-center gap-1">
                                          <ExternalLink className="w-3 h-3" /> PDF
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Questions with Reply */}
                          {projectDocs[project.id].offers?.some(o => o.questions?.length > 0) && (
                            <div>
                              <h4 className="text-xs font-bold uppercase text-lab-text-muted mb-2" style={{letterSpacing:"0.1em"}}>Rückfragen & Antworten</h4>
                              {projectDocs[project.id].offers.filter(o => o.questions?.length > 0).map(o => (
                                <div key={o.offer_number} className="space-y-1.5 mb-3">
                                  {o.questions.map((q, i) => (
                                    <div key={q.asked_at || i} className="bg-white border border-lab-border p-3">
                                      {/* Customer question */}
                                      {q.question && (
                                        <div className="flex items-start gap-2 text-xs">
                                          <MessageSquare className="w-3 h-3 text-lab-accent mt-0.5 flex-shrink-0" />
                                          <div className="flex-1">
                                            <span className="text-lab-primary">{q.question}</span>
                                            <span className="text-lab-text-muted ml-2">({o.offer_number}, {q.asked_at ? new Date(q.asked_at).toLocaleDateString("de-CH") : ""})</span>
                                          </div>
                                        </div>
                                      )}
                                      {/* Admin reply */}
                                      {q.reply ? (
                                        <div className="mt-2 ml-5 bg-[#F0FDF4] border border-green-200 p-2 text-xs flex items-start gap-2">
                                          <ArrowRight className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                          <div>
                                            <span className="text-green-800">{q.reply.reply || q.reply}</span>
                                            <span className="text-green-600/60 ml-2">{q.reply.replied_at ? new Date(q.reply.replied_at).toLocaleDateString("de-CH") : ""}</span>
                                          </div>
                                        </div>
                                      ) : q.question && (
                                        <AdminReplyBox projectId={project.id} offerNumber={o.offer_number} questionIndex={i}
                                          onReplied={() => {
                                            setLoadingDocs(project.id);
                                            axios.get(`${API}/admin/projects/${project.id}/documents`).then(res => {
                                              setProjectDocs(prev => ({...prev, [project.id]: res.data}));
                                            }).finally(() => setLoadingDocs(null));
                                          }} />
                                      )}
                                      {/* Admin message (not reply to specific question) */}
                                      {q.type === "message" && (
                                        <div className="flex items-start gap-2 text-xs">
                                          <ArrowRight className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                          <div>
                                            <span className="text-green-800">{q.reply?.reply || q.reply}</span>
                                            <span className="text-green-600/60 ml-2">{q.replied_at ? new Date(q.replied_at).toLocaleDateString("de-CH") : ""}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Structured Briefing View */}
                    {viewingProject === project.id && projectDetail && (
                      <PortalBriefing
                        detail={projectDetail}
                        onAddNote={async (text) => {
                          const res = await axios.post(`${API}/admin/projects/${project.id}/notes`, { text });
                          setProjectDetail(prev => ({
                            ...prev,
                            project: {
                              ...prev.project,
                              admin_notes: [...(prev.project.admin_notes || []), res.data.note]
                            }
                          }));
                        }}
                        onStatusChange={async (newStatus) => {
                          await axios.put(`${API}/admin/projects/${project.id}`, { project_status: newStatus });
                          setProjectDetail(prev => ({
                            ...prev,
                            project: { ...prev.project, project_status: newStatus }
                          }));
                          toast.success("Status aktualisiert");
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-8">
              <h2 className="font-playfair text-xl font-semibold text-lab-primary">
                Einstellungen
              </h2>

              {/* Company Settings */}
              {companySettings && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-lab-primary">
                    <Building2 className="w-5 h-5 text-lab-accent" />
                    <h3 className="font-semibold">Firmendaten</h3>
                  </div>
                  <p className="text-sm text-lab-text-muted">
                    Diese Daten werden auf dem Impressum, der Datenschutzseite und im Footer angezeigt.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Unternehmensname</label>
                      <Input value={companySettings.company_name || ""} onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})} className="rounded-none border-lab-border" data-testid="company-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Unternehmensform</label>
                      <Input value={companySettings.company_form || ""} onChange={(e) => setCompanySettings({...companySettings, company_form: e.target.value})} className="rounded-none border-lab-border" data-testid="company-form-input" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Strasse & Nr.</label>
                      <Input value={companySettings.street || ""} onChange={(e) => setCompanySettings({...companySettings, street: e.target.value})} className="rounded-none border-lab-border" data-testid="company-street-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">PLZ & Ort</label>
                      <Input value={companySettings.zip_city || ""} onChange={(e) => setCompanySettings({...companySettings, zip_city: e.target.value})} className="rounded-none border-lab-border" data-testid="company-zip-city-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Land</label>
                      <Input value={companySettings.country || ""} onChange={(e) => setCompanySettings({...companySettings, country: e.target.value})} className="rounded-none border-lab-border" data-testid="company-country-input" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">E-Mail</label>
                      <Input value={companySettings.email || ""} onChange={(e) => setCompanySettings({...companySettings, email: e.target.value})} className="rounded-none border-lab-border" data-testid="company-email-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Telefon</label>
                      <Input value={companySettings.phone || ""} onChange={(e) => setCompanySettings({...companySettings, phone: e.target.value})} className="rounded-none border-lab-border" data-testid="company-phone-input" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Verantwortlicher (Name)</label>
                      <Input value={companySettings.responsible_name || ""} onChange={(e) => setCompanySettings({...companySettings, responsible_name: e.target.value})} className="rounded-none border-lab-border" data-testid="company-responsible-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Verantwortlicher (Adresse)</label>
                      <Input value={companySettings.responsible_address || ""} onChange={(e) => setCompanySettings({...companySettings, responsible_address: e.target.value})} className="rounded-none border-lab-border" data-testid="company-responsible-address-input" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">UID-Nummer</label>
                      <Input value={companySettings.uid_number || ""} onChange={(e) => setCompanySettings({...companySettings, uid_number: e.target.value})} className="rounded-none border-lab-border" data-testid="company-uid-input" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-lab-primary">Handelsregistereintrag</label>
                      <Input value={companySettings.trade_register || ""} onChange={(e) => setCompanySettings({...companySettings, trade_register: e.target.value})} className="rounded-none border-lab-border" data-testid="company-trade-register-input" />
                    </div>
                  </div>

                  <button
                    onClick={saveCompanySettings}
                    disabled={savingCompany}
                    className="btn-primary rounded-none flex items-center gap-2 px-6 disabled:opacity-50"
                    data-testid="save-company-settings-btn"
                  >
                    <Save className="w-4 h-4" />
                    {savingCompany ? "Speichern..." : "Firmendaten speichern"}
                  </button>
                </div>
              )}

              <div className="border-t border-lab-border" />
              
              {/* Webhook Configuration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lab-primary">
                  <Webhook className="w-5 h-5 text-lab-accent" />
                  <h3 className="font-semibold">Webhook-Benachrichtigung</h3>
                </div>
                <p className="text-sm text-lab-text-muted">
                  Bei jeder neuen Terminbuchung wird ein POST-Request mit allen Kundendaten an die unten konfigurierte URL gesendet.
                  Ideal für Integrationen mit Make.com, Zapier oder eigene Systeme.
                </p>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-lab-primary">Webhook URL</label>
                  <div className="flex gap-3">
                    <Input
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://hook.eu2.make.com/... oder https://hooks.zapier.com/..."
                      className="rounded-none border-lab-border flex-1"
                      data-testid="webhook-url-input"
                    />
                    <button
                      onClick={saveSettings}
                      disabled={savingSettings}
                      className="btn-primary rounded-none flex items-center gap-2 px-6 disabled:opacity-50"
                      data-testid="save-settings-btn"
                    >
                      <Save className="w-4 h-4" />
                      {savingSettings ? "Speichern..." : "Speichern"}
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={useInternalWebhook}
                      className="text-xs text-lab-accent hover:underline flex items-center gap-1"
                      data-testid="use-internal-webhook-btn"
                    >
                      <Copy className="w-3 h-3" />
                      Interne Test-URL verwenden
                    </button>
                    <span className="text-xs text-lab-text-muted">|</span>
                    <button
                      onClick={testWebhook}
                      disabled={testingWebhook || !webhookUrl}
                      className="text-xs text-lab-accent hover:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline"
                      data-testid="test-webhook-btn"
                    >
                      <Play className="w-3 h-3" />
                      {testingWebhook ? "Teste..." : "Test-Webhook senden"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Webhook Delivery Log */}
              <div className="space-y-4 pt-4 border-t border-lab-border">
                <h3 className="font-semibold text-lab-primary flex items-center gap-2">
                  <Clock className="w-4 h-4 text-lab-accent" />
                  Zustellungsprotokoll
                </h3>
                {webhookLogs.length === 0 ? (
                  <p className="text-sm text-lab-text-muted py-4 text-center">Noch keine Webhook-Zustellungen.</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {webhookLogs.map((log) => (
                      <div key={log.id} className={`p-3 border text-sm ${
                        log.status === "success" 
                          ? "bg-green-50 border-green-200" 
                          : "bg-red-50 border-red-200"
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {log.status === "success" ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className={log.status === "success" ? "text-green-800" : "text-red-800"}>
                              {log.status === "success" ? "Erfolgreich" : "Fehlgeschlagen"} ({log.status_code})
                            </span>
                          </div>
                          <span className="text-xs text-lab-text-muted">
                            {new Date(log.timestamp).toLocaleString('de-CH')}
                          </span>
                        </div>
                        <p className="text-xs text-lab-text-muted mt-1 truncate">
                          {log.appointment_name} &rarr; {log.url}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Received Webhooks (Internal Test) */}
              {receivedWebhooks.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-lab-border">
                  <h3 className="font-semibold text-lab-primary flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-lab-accent" />
                    Empfangene Webhooks (interner Test)
                  </h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {receivedWebhooks.map((entry) => (
                      <div key={entry.id} className="p-3 bg-lab-bg-light border border-lab-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Empfangen
                          </span>
                          <span className="text-xs text-lab-text-muted">
                            {new Date(entry.received_at).toLocaleString('de-CH')}
                          </span>
                        </div>
                        <pre className="text-xs bg-white p-2 border border-lab-border overflow-x-auto max-h-48">
                          {JSON.stringify(entry.payload, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payload Example */}
              <div className="bg-lab-bg-light p-4 border border-lab-border">
                <p className="text-xs font-semibold text-lab-text-muted uppercase tracking-wider mb-2">Webhook-Payload Beispiel</p>
                <pre className="text-xs text-lab-text overflow-x-auto bg-white p-3 border border-lab-border">
{`{
  "event": "appointment_booked",
  "timestamp": "2026-03-25T10:00:00Z",
  "appointment": {
    "date": "2026-03-26",
    "time": "11:00",
    "topic": "ki-automatisierung"
  },
  "customer": {
    "name": "Max Mustermann",
    "email": "max@beispiel.ch",
    "phone": "+41 79 ...",
    "position": "CTO",
    "linkedin": "https://..."
  },
  "company": {
    "name": "Firma AG",
    "size": "11-50",
    "website": "https://...",
    "industry": "it-software"
  },
  "questionnaire": { ... }
}`}
                </pre>
              </div>
            </div>
          )}
        </div>
        )}
        </main>
      </div>
    </div>
  );
}
